import { useState, useEffect, useRef, useCallback } from 'react';
import { config } from '../config/config';

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface UseAIChatReturn {
  isConnected: boolean;
  messages: ChatMessage[];
  currentResponse: string;
  isStreaming: boolean;
  error: string | null;
  sendMessage: (content: string) => void;
  createNewSession: (title?: string) => void;
  loadHistory: (sessionId: number) => void;
  reconnect: () => void;
  clearError: () => void;
}

export const useAIChat = (token: string | null, sessionId: number | null = null): UseAIChatReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentResponse, setCurrentResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Convert HTTP URL to WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    const baseUrl = config.API_BASE_URL;
    const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
    const wsBase = baseUrl.replace(/^https?/, wsProtocol);
    
    // URL-encode the token to handle special characters
    const encodedToken = encodeURIComponent(token || '');
    let url = `${wsBase}/ws/ai/chat/?token=${encodedToken}`;
    if (sessionId) {
      url += `&session_id=${sessionId}`;
    }
    return url;
  }, [token, sessionId]);

  // Handle incoming messages
  const handleMessage = useCallback((data: any) => {
    switch (data.type) {
      case 'connected':
        console.log('Connected:', data.message);
        break;

      case 'message_received':
        // User message acknowledged
        setMessages((prev) => [
          ...prev,
          {
            id: data.message_id,
            role: 'user',
            content: data.content,
            timestamp: new Date(),
          },
        ]);
        break;

      case 'stream_start':
        setIsStreaming(true);
        setCurrentResponse('');
        break;

      case 'stream_chunk':
        // Append chunk to current response
        setCurrentResponse((prev) => prev + data.content);
        break;

      case 'stream_end':
        // Finalize the assistant message
        setIsStreaming(false);
        setMessages((prev) => [
          ...prev,
          {
            id: data.message_id,
            role: 'assistant',
            content: data.full_content,
            timestamp: new Date(),
          },
        ]);
        setCurrentResponse('');
        break;

      case 'error':
        setError(data.message);
        setIsStreaming(false);
        console.error('AI Chat error:', data.message);
        break;

      case 'history_loaded':
        // Load conversation history
        if (data.messages) {
          const formattedMessages: ChatMessage[] = data.messages.map((msg: any) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.created_at),
          }));
          setMessages(formattedMessages);
        }
        break;

      default:
        console.log('Unknown message type:', data.type);
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!token || token.trim() === '') {
      setError('No authentication token provided. Please login again.');
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      const url = getWebSocketUrl();
      console.log('Connecting to WebSocket:', url.replace(/token=[^&]+/, 'token=***'));
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('AI Chat WebSocket connected');
        setIsConnected(true);
        setError(null);
      };

      ws.onmessage = (event) => {
        try {
          const messageText = event.data;
          
          // Check if it's a plain text error message
          if (typeof messageText === 'string' && 
              (messageText.toLowerCase().includes('authentication') || 
               messageText.toLowerCase().includes('token') ||
               messageText.toLowerCase().includes('unauthorized') ||
               messageText.toLowerCase().includes('failed'))) {
            setError(messageText || 'Authentication failed. Please check your token or login again.');
            setIsConnected(false);
            ws.close();
            return;
          }
          
          const data = JSON.parse(messageText);
          
          // Handle authentication errors that come as JSON messages
          if (data.type === 'error' && data.message) {
            const errorMsg = data.message.toLowerCase();
            if (errorMsg.includes('authentication') || 
                errorMsg.includes('token') ||
                errorMsg.includes('unauthorized') ||
                errorMsg.includes('failed')) {
              setError(data.message || 'Authentication failed. Please check your token or login again.');
              setIsConnected(false);
              ws.close();
              return;
            }
          }
          
          handleMessage(data);
        } catch (err) {
          // If parsing fails, check if it's an error message
          const messageText = event.data;
          if (typeof messageText === 'string' && messageText.length < 200) {
            // Might be a plain text error
            if (messageText.toLowerCase().includes('authentication') || 
                messageText.toLowerCase().includes('token') ||
                messageText.toLowerCase().includes('failed')) {
              setError(messageText);
              setIsConnected(false);
              ws.close();
              return;
            }
          }
          console.error('Error parsing WebSocket message:', err, event.data);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setError('Connection error occurred');
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);

        // Handle authentication failure close codes
        // 1008 = Policy Violation (often used for auth failures)
        // 4001-4003 = Custom authentication error codes
        if (event.code === 1008 || (event.code >= 4001 && event.code <= 4003)) {
          const errorMsg = event.reason || 'Authentication failed. Please check your token or login again.';
          setError(errorMsg);
          // Don't attempt to reconnect on auth failures
          return;
        }

        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting to reconnect...');
            connect();
          }, 3000);
        }
      };
    } catch (err) {
      console.error('Error creating WebSocket:', err);
      setError('Failed to connect to AI chat');
    }
  }, [token, getWebSocketUrl, handleMessage]);

  // Send a chat message
  const sendMessage = useCallback((content: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setError('WebSocket is not connected');
      return;
    }

    if (!content || !content.trim()) {
      setError('Message cannot be empty');
      return;
    }

    try {
      wsRef.current.send(
        JSON.stringify({
          type: 'message',
          content: content.trim(),
        })
      );
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    }
  }, []);

  // Create a new session
  const createNewSession = useCallback((title: string = 'Новый чат') => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setError('WebSocket is not connected');
      return;
    }

    try {
      wsRef.current.send(
        JSON.stringify({
          type: 'new_session',
          title: title,
        })
      );
      setMessages([]);
    } catch (err) {
      console.error('Error creating session:', err);
      setError('Failed to create new session');
    }
  }, []);

  // Load conversation history
  const loadHistory = useCallback((sessionId: number) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setError('WebSocket is not connected');
      return;
    }

    try {
      wsRef.current.send(
        JSON.stringify({
          type: 'load_history',
          session_id: sessionId,
        })
      );
    } catch (err) {
      console.error('Error loading history:', err);
      setError('Failed to load conversation history');
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Connect on mount or when token changes
  useEffect(() => {
    if (token) {
      connect();
    }

    return () => {
      // Cleanup
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [token, connect]);

  return {
    isConnected,
    messages,
    currentResponse,
    isStreaming,
    error,
    sendMessage,
    createNewSession,
    loadHistory,
    reconnect: connect,
    clearError,
  };
};

